import React, { useState } from 'react';
import './QuestionTableModal.css';
import {
  FormItem,
  FormQuestionItem,
  isPageBreak,
  QUESTION_TYPE_MAP_TRANSLATION,
  Condition,
  OPERATOR_OPTIONS,
} from '../../types';
import {
  formatMultipleSelectLabel,
  hiddenQuestionMark,
  isIncludedInQuestionTable,
} from '../../utils/questionTableView';

interface QuestionTableModalProps {
  questions: FormItem[];
  onClose: () => void;
  formName: string;
}

interface TableRow {
  componentType: string;
  key: string;
  required: string;
  multipleSelect: string;
  conditionalQuestion: string;
  conditions: string;
  exclusive: string;
  description: string;
  objectName: string;
  hidden: string;
  isHidden: boolean;
}

const TABLE_LABELS = {
  COMPONENT_TYPE: 'סוג רכיב',
  KEY: 'מפתח',
  REQUIRED: 'חובה',
  MULTIPLE_SELECT: 'בחירה מרובה',
  CONDITIONAL_QUESTION: 'קיים תנאי',
  CONDITIONS: 'תנאים',
  EXCLUSIVE: 'בלעדי',
  DESCRIPTION: 'הסבר',
  OBJECT_NAME: 'שם האובייקט',
  TABLE_VIEW: 'תצוגת טבלה',
  COPY_TABLE: 'העתק טבלה',
  SHOW_HIDDEN: 'הצג שאלות מוסתרות',
  HIDDEN: 'מוסתר',
  TABLE_COPIED: 'הטבלה הועתקה ללוח!',
  COPY_ERROR: 'שגיאה בהעתקה',
};

const QuestionTableModal: React.FC<QuestionTableModalProps> = ({
  questions,
  onClose,
  formName,
}) => {
  const [showHiddenQuestions, setShowHiddenQuestions] = useState(false);

  const decodeHtmlAndStripTags = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    const decoded = textarea.value;
    return decoded.replace(/<[^>]*>/g, '');
  };

  const formatConditions = (
    conditions: Condition[] | undefined,
    allQuestions: FormItem[],
  ): string => {
    if (!conditions || conditions.length === 0) {
      return '';
    }

    const formattedConditions: string[] = [];
    const answerableQuestions = allQuestions.filter((q): q is FormQuestionItem => !isPageBreak(q));

    conditions.forEach((condition, index) => {
      const connectedQuestion = answerableQuestions.find(
        (q) => q.guid === condition.connected_question_guid,
      );
      const questionKey = connectedQuestion?.question_key || '';

      if (questionKey) {
        // Get operator label
        const operatorOption = OPERATOR_OPTIONS.find((opt) => opt.value === condition.operator);
        const operatorLabel = operatorOption?.label || condition.operator || '';

        // Convert GUID values to option text
        let valuesText = '';
        if (condition.values && condition.values.length > 0) {
          if (condition.operator === 'contains' || condition.operator === 'not_contains') {
            // For contains/not_contains, values are GUIDs that need to be converted to option text
            const optionTexts = condition.values
              .map((guid) => {
                const option = connectedQuestion?.options?.find((opt) => opt.guid === guid);
                return option ? decodeHtmlAndStripTags(option.text.toString()) : guid;
              })
              .filter((text) => text); // Remove empty values
            valuesText = optionTexts.length > 0 ? `: ${optionTexts.join(', ')}` : '';
          } else {
            // For greater_than/less_than, values are numbers
            valuesText = condition.values.length > 0 ? `: ${condition.values.join(', ')}` : '';
          }
        }

        const conditionText = `${questionKey} - ${operatorLabel}${valuesText}`;

        if (index === 0) {
          formattedConditions.push(conditionText);
        } else {
          // Get connector - all conditions after the first use the connector from condition[1]
          // 'and' means 'וגם', 'or' means 'או'
          const connector =
            conditions[1]?.connector && conditions[1].connector !== 'first'
              ? conditions[1].connector
              : condition.connector && condition.connector !== 'first'
                ? condition.connector
                : 'and';
          const connectorText = connector === 'or' ? 'או' : 'וגם';
          formattedConditions.push(connectorText);
          formattedConditions.push(conditionText);
        }
      }
    });

    return formattedConditions.join(' ');
  };

  const transformQuestionsToTableData = (
    questions: FormItem[],
    showHiddenQuestions: boolean,
  ): TableRow[] => {
    const tableRows: TableRow[] = [];

    questions
      .filter(
        (q): q is FormQuestionItem =>
          !isPageBreak(q) && isIncludedInQuestionTable(q, showHiddenQuestions),
      )
      .sort((a, b) => a.question_index - b.question_index)
      .forEach((question) => {
        const questionType =
          QUESTION_TYPE_MAP_TRANSLATION[question.question_type || ''] ||
          question.question_type ||
          '';
        const questionKey = question.question_key || '';
        const isRequired = question.mandatory ? 'V' : '';
        const hasConditions =
          question.appeared_conditions && question.appeared_conditions.length > 0;
        const conditionalQuestion = hasConditions ? 'V' : '';
        const conditionsText = formatConditions(question.appeared_conditions, questions);
        const isHidden = question.show_question === false;
        const hidden = hiddenQuestionMark(question.show_question);

        if (
          question.question_type === 'radio' ||
          question.question_type === 'checkbox' ||
          question.question_type === 'select'
        ) {
          // First row with question text
          const questionDescription = question.description
            ? decodeHtmlAndStripTags(question.description)
            : '';
          tableRows.push({
            componentType: questionType,
            key: questionKey,
            required: isRequired,
            multipleSelect: formatMultipleSelectLabel(question),
            conditionalQuestion: conditionalQuestion,
            conditions: conditionsText,
            exclusive: '',
            description: questionDescription,
            objectName: decodeHtmlAndStripTags(question.text),
            hidden,
            isHidden,
          });

          // Additional rows for each option
          question.options?.forEach((option) => {
            const isExclusive = option.exclusive ? 'V' : '';
            tableRows.push({
              componentType: '',
              key: '',
              required: '',
              multipleSelect: '',
              conditionalQuestion: '',
              conditions: '',
              exclusive: isExclusive,
              description: '',
              objectName: decodeHtmlAndStripTags(option.text.toString()),
              hidden: '',
              isHidden,
            });
          });
        } else if (question.question_type === 'scale') {
          const scaleInfo = `${question.scale_min_text || ''} - ${question.scale_max_text || ''} (${question.scale_num_options || question.max_value || ''})`;
          const questionDescription = question.description
            ? decodeHtmlAndStripTags(question.description)
            : '';
          tableRows.push({
            componentType: questionType,
            key: questionKey,
            required: isRequired,
            multipleSelect: formatMultipleSelectLabel(question),
            conditionalQuestion: conditionalQuestion,
            conditions: conditionsText,
            exclusive: '',
            description: questionDescription,
            objectName: `${decodeHtmlAndStripTags(question.text)}\n${scaleInfo}`,
            hidden,
            isHidden,
          });
        } else {
          // Text questions and others
          const questionDescription = question.description
            ? decodeHtmlAndStripTags(question.description)
            : '';
          const questionText = decodeHtmlAndStripTags(question.text);
          const hasMeasurementUnit =
            question.field_type === 'number' &&
            question.measurement_unit != null &&
            String(question.measurement_unit).trim() !== '';
          const objectName = hasMeasurementUnit
            ? `${questionText} (ב-${question.measurement_unit})`
            : questionText;
          tableRows.push({
            componentType: questionType,
            key: questionKey,
            required: isRequired,
            multipleSelect: formatMultipleSelectLabel(question),
            conditionalQuestion: conditionalQuestion,
            conditions: conditionsText,
            exclusive: '',
            description: questionDescription,
            objectName,
            hidden,
            isHidden,
          });
        }
      });

    return tableRows;
  };

  const tableData = transformQuestionsToTableData(questions, showHiddenQuestions);

  const copyToClipboard = () => {
    const table = document.getElementById('questions-table') as HTMLTableElement;
    if (!table) {
      alert(TABLE_LABELS.COPY_ERROR);
      return;
    }

    const html = table.outerHTML;
    const text = Array.from(table.rows)
      .map((row) =>
        Array.from(row.cells)
          .map((cell) => cell.innerText)
          .join('\t'),
      )
      .join('\n');

    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ])
      .then(() => {
        alert(TABLE_LABELS.TABLE_COPIED);
      })
      .catch(() => {
        alert(TABLE_LABELS.COPY_ERROR);
      });
  };

  return (
    <div className='table-modal-overlay'>
      <div className='table-modal'>
        <div className='table-modal-header'>
          <h2>
            {TABLE_LABELS.TABLE_VIEW} - {formName}
          </h2>
          <button className='table-modal-close' onClick={onClose}>
            ×
          </button>
        </div>

        <div className='table-modal-actions'>
          <button className='copy-table-btn' onClick={copyToClipboard}>
            {TABLE_LABELS.COPY_TABLE}
          </button>
          <label className='show-hidden-questions-label'>
            <input
              type='checkbox'
              checked={showHiddenQuestions}
              onChange={(event) => setShowHiddenQuestions(event.target.checked)}
            />
            {TABLE_LABELS.SHOW_HIDDEN}
          </label>
        </div>

        <div className='table-modal-body'>
          <table id='questions-table' className='questions-table'>
            <thead>
              <tr>
                <th>{TABLE_LABELS.COMPONENT_TYPE}</th>
                <th>{TABLE_LABELS.KEY}</th>
                <th>{TABLE_LABELS.REQUIRED}</th>
                <th>{TABLE_LABELS.MULTIPLE_SELECT}</th>
                <th>{TABLE_LABELS.EXCLUSIVE}</th>
                <th>{TABLE_LABELS.OBJECT_NAME}</th>
                <th>{TABLE_LABELS.DESCRIPTION}</th>
                <th>{TABLE_LABELS.CONDITIONAL_QUESTION}</th>
                <th>{TABLE_LABELS.CONDITIONS}</th>
                {showHiddenQuestions && <th>{TABLE_LABELS.HIDDEN}</th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => {
                const isQuestion = row.componentType !== '';
                const cellStyle = {
                  fontWeight: isQuestion ? 'bold' : 'normal',
                };
                return (
                  <tr
                    key={index}
                    className={row.isHidden ? 'questions-table-row--hidden' : undefined}
                  >
                    <td style={cellStyle}>{row.componentType}</td>
                    <td style={cellStyle}>{row.key}</td>
                    <td style={cellStyle}>{row.required}</td>
                    <td style={cellStyle}>{row.multipleSelect}</td>
                    <td style={cellStyle}>{row.exclusive}</td>
                    <td style={{ whiteSpace: 'pre-line', ...cellStyle }}>{row.objectName}</td>
                    <td style={{ whiteSpace: 'pre-line', ...cellStyle }}>{row.description}</td>
                    <td style={cellStyle}>{row.conditionalQuestion}</td>
                    <td style={cellStyle}>{row.conditions}</td>
                    {showHiddenQuestions && <td style={cellStyle}>{row.hidden}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuestionTableModal;
